import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_pond-education-hub/artifacts/bt49zksc_ed5f945d-1989-4e14-ba5b-09256b7ca05c.png";

export const Hero = () => {
  const ref = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", prefersReducedMotion ? "0%" : "12%"]);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="hero" data-testid="hero-section" ref={ref} className="relative min-h-[92vh] overflow-hidden">
      {/* Cinematic full-bleed image with slow parallax */}
      <motion.div style={{ y }} className="absolute inset-0 img-grade">
        <img
          src={HERO_IMAGE}
          alt="Morning mist settling over a prairie pond at sunrise in Alberta"
          className="editorial-img scale-[1.08]"
          loading="eager"
        />
      </motion.div>
      {/* Legibility overlay, bottom-weighted */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10" aria-hidden="true" />
      {/* Mobile: slightly darken the whole image for text readability */}
      <div className="lg:hidden absolute inset-0 bg-black/40" aria-hidden="true" />
      {/* Desktop: darken left side for text readability */}
      <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-black/45 via-black/15 to-transparent" aria-hidden="true" />

      <div className="relative mx-auto w-full max-w-[1280px] px-5 sm:px-8 min-h-[92vh] flex flex-col">
        <div className="flex-1" />

        <div className="pb-14 sm:pb-20">
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-mono-meta text-[10px] sm:text-xs text-white/75">
              Independent education · Training · Consulting
            </p>

            <h1 className="mt-6 font-serif-heading font-medium text-[clamp(3rem,7vw+0.5rem,6.5rem)] lg:text-[clamp(3rem,4.2vw+0.5rem,5rem)] leading-[0.98] tracking-[-0.02em] text-white max-w-[13ch] lg:max-w-none">
              Learn the water.
              <br className="hidden lg:block" /> Then build the pond.
            </h1>

            <div className="mt-8 sm:mt-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
              <div>
                <p className="text-base sm:text-lg leading-relaxed text-white/85 max-w-[52ch]">
                  We help Alberta landowners and stewards understand pond
                  aquaculture before they invest in it &mdash; with honest,
                  independent guidance shaped by this province's land, water,
                  and winters.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Button
                    data-testid="hero-primary-cta-button"
                    size="lg"
                    onClick={() => scrollTo("#book")}
                    className="btn-lift rounded-none bg-[color:var(--apn-paper-2)] text-[color:var(--apn-forest)] hover:bg-white h-12 px-8 text-[15px] font-medium"
                  >
                    Book a free consultation
                  </Button>
                  <Button
                    data-testid="hero-secondary-cta-button"
                    size="lg"
                    variant="outline"
                    onClick={() => scrollTo("#programs")}
                    className="btn-lift rounded-none border-white/45 bg-transparent text-white hover:bg-white/10 hover:text-white h-12 px-8 text-[15px]"
                  >
                    Explore the learning path
                  </Button>
                </div>
              </div>

              <div className="hidden lg:block shrink-0 border-l border-white/25 pl-6 max-w-[38ch]">
                <p className="text-sm leading-relaxed text-white/80">
                  We provide education and consulting — not fish, equipment, or
                  pond construction.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

    </section>
  );
};
